﻿// noinspection JSUnusedGlobalSymbols

import {GeminiLiveOutput} from "./GeminiLiveOutput";
import {GeminiLiveInput} from "./GeminiLiveInput";
import {Trace, Error, Delay, Catch} from "../../Utilities";
import {Sounds} from "../../Sounds";

let Socket: WebSocket;
let Connected: boolean;
let Dotnet: any;
let lock: WakeLockSentinel | null = null;
let pending: Promise<void> | null = null;
let active = false;

async function RequestLock(): Promise<void> {
    if (!active || lock || pending || document.visibilityState !== "visible" || !("wakeLock" in navigator)) return;

    pending = (async () => {
        try {
            const next = await navigator.wakeLock.request("screen");

            if (!active) {
                await next.release();
                return;
            }

            lock = next;
            next.addEventListener("release", () => {
                if (lock === next) lock = null;
            });
        } catch (error) {
            console.warn("Could not acquire the screen wake lock.", error);
        } finally {
            pending = null;
        }
    })();

    await pending;
}

async function AcquireLock(): Promise<void> {
    active = true;
    await RequestLock();
}

async function ReleaseLock(): Promise<void> {
    active = false;

    const current = lock;
    lock = null;

    if (!current || current.released) return;

    try {
        await current.release();
    } catch (error) {
        console.warn("Could not release the screen wake lock.", error);
    }
}

document.addEventListener("visibilitychange", () => {
    if (active && document.visibilityState === "visible") void RequestLock();
});

async function Connect(dotnet: any, url: string, speed: number) {
    
    Trace("Connecting...");
    Dotnet = dotnet;
    
    // Close existing connection
    if (Connected) {
        await ReleaseLock();
        Connected = false;
        try 
        { 
            Socket.close();
            GeminiLiveInput.Deinitialize();
            GeminiLiveOutput.Deinitialize();
            await Delay(2000);
        } catch { }   
        Trace("Existing connection closed");
    }
       
    try {
        // Capture audio from microphone
        await GeminiLiveInput.Capture(bytes => {
            if (!Connected) return;
            Socket.send(bytes);
        });
        Trace("Microphone capture started successfully...");
    }
    catch (e) {
        console.error(e);
        Socket.close();
        Connected = false;
        Trace("Microphone capture failed");
        return false;
    }

    // Prepare audio output
    await GeminiLiveOutput.Initialize(speed);
    
    try {
        Trace("Connecting to websocket...");
        
        Socket = new WebSocket('wss://' + location.host + url);
        Socket.binaryType = 'arraybuffer';
        Socket.onopen = () => OnConnect();
        Socket.onclose = () => OnDisconnect();
        Socket.onerror = (error: Event) => Error(error);

        // Handle incoming audio stream
        Socket.onmessage = (event: MessageEvent) => {
            if (event.data instanceof ArrayBuffer) {
                GeminiLiveOutput.Play(event.data);
            }
        };
    }
    catch (e) {
        Trace("Failed to connect to websocket", e);
        GeminiLiveInput.Deinitialize();
        GeminiLiveOutput.Deinitialize();
        Socket?.close();
        throw e;
    }
    
    // Await for socket is really connected
    while (Socket.readyState == WebSocket.CONNECTING) {
        await Delay(10);
    }
    
    if (Socket.readyState != WebSocket.OPEN){
        Trace("Failed to connect to websocket");
        return false;  
    }

    await AcquireLock();
        
    return true;
}

function OnConnect() {
    if (Connected) return;
    Connected = true;
    Sounds.Beep();    
    Trace("Connected");
}

function OnDisconnect() {
    if (!Connected) return;
    Connected = false;
    void ReleaseLock();
    
    // Close socket if still open (preserve socket context)
    Catch(() => Socket?.close());
        
    try {
        GeminiLiveInput.Deinitialize();
        GeminiLiveOutput.Deinitialize();
        Sounds.Beep();
    }
    finally {
        Trace("Disconnected");
    }
}

async function Interrupt() {
    Trace("Interrupt command received from the server");
    GeminiLiveOutput.Interrupt();
}

function SetSpeed(speed: number) {
    GeminiLiveOutput.SetSpeed(speed);
}

function Disconnect(){
    Trace("Disconnect notification received from the server");
    OnDisconnect();
}

export const GeminiLiveAgent = {
    Connect,
    Disconnect,
    Interrupt,
    SetSpeed
}
